import { useMutation, useQuery } from '@apollo/react-hooks';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import { Editor, EditorContext, WithEditorActions } from '@atlaskit/editor-core';
import { JIRATransformer } from '@atlaskit/editor-jira-transformer';
import Flag, { FlagGroup } from '@atlaskit/flag';
import Form, { CheckboxField, Field, Fieldset } from '@atlaskit/form';
import Tick from '@atlaskit/icon/glyph/check-circle';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import PageHeader from '@atlaskit/page-header';
import { FlexContainer } from '@atlaskit/right-side-panel';
import Select from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import { PijenDemo } from '@jexo/pijen';
import { debounce } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    CREATE_NOTIFICATION,
    DELETE_NOTIFICATION,
    TOGGLE_NOTIFICATION_PUBLISHED,
    UPDATE_NOTIFICATION,
} from '../../graphql/mutations';
import { NOTIFICATION_TYPES_QUERY, NOTIFICATIONS_QUERY } from '../../graphql/queries';
import { getTextColor } from '../common/helpers';
import '@jexo/pijen/dist/index.css';

export const RED = '#DE350B';
export const GREEN = '#00875A';

const PageWrapper = styled.div`
    padding: 0 24px;
    width: 100%;
`;

const ArrowLabelWrapper = styled.div`
    padding-top: 2px;
    padding-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ContentWrapper = styled.div`
    width: 100%;
    display: flex;
    .notification-form-wrapper {
        padding-right: 40px;
        flex-grow: 1;
    }
    .preview-wrapper {
        min-width: 400px;
    }
`;

const StatusLabel = ({ status }) => {
    const appearance = status === 'published' ? 'success' : 'default';
    return (
        <div style={{ paddingTop: '5px', display: 'flex' }}>
            <span style={{ paddingRight: '5px' }}>Status </span>
            <Lozenge appearance={appearance}>
                <p style={{ margin: 0, padding: '5px 10px' }}>{status}</p>
            </Lozenge>
        </div>
    );
};

const typesColorStyles = {
    control: (styles) => ({ ...styles, backgroundColor: 'white' }),
    singleValue: (styles, { data }) => ({
        ...styles,
        backgroundColor: data.color,
        color: getTextColor(data.color),
        borderRadius: '3px',
        padding: '5px 10px',
    }),
};

function NotificationDetails({ notification, widgetId, setNotificationForUpdate, closePage }) {
    const [title, setTitle] = useState('');
    const [type, setType] = useState('');
    const [image, setImage] = useState('');
    const [description, setDescription] = useState('');
    const [detailDescription, setDetailDescription] = useState(notification ? notification.detailDescription : '');
    const [showFeedbackField, setShowFeedbackField] = useState(null);
    const [showDetailDescriptionField, setShowDetailDescriptionField] = useState(
        notification ? notification.showDetailView : false,
    );
    const [saveLoading, setSaveLoading] = useState(false);
    const [publishLoading, setPublishLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [flags, setFlags] = useState([]);
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [formData, setFormData] = useState({});

    // graphql
    const { data: notificationTypesData, loading: notificationTypesLoading } = useQuery(NOTIFICATION_TYPES_QUERY);
    const [createNotification] = useMutation(CREATE_NOTIFICATION, {
        update(cache, { data: { createNotification: createNotificationData } }) {
            const { notifications } = cache.readQuery({ query: NOTIFICATIONS_QUERY, variables: { widgetId } });
            cache.writeQuery({
                query: NOTIFICATIONS_QUERY,
                variables: { widgetId },
                data: { notifications: notifications.concat([createNotificationData]) },
            });
        },
    });
    const [deleteNotification] = useMutation(DELETE_NOTIFICATION, {
        update(cache, { data: { deleteNotification: deleteNotificationData } }) {
            const { notifications } = cache.readQuery({ query: NOTIFICATIONS_QUERY, variables: { widgetId } });
            cache.writeQuery({
                query: NOTIFICATIONS_QUERY,
                variables: { widgetId },
                data: { notifications: notifications.filter((el) => el.id !== deleteNotificationData.id) },
            });
        },
    });
    const [updateNotification] = useMutation(UPDATE_NOTIFICATION);
    const [toggleNotificationPublished] = useMutation(TOGGLE_NOTIFICATION_PUBLISHED);

    const addFlag = (flagType) => {
        const flag = {
            id: 'success',
            key: 'success',
            title: `Notification ${flagType}`,
            icon: <Tick primaryColor={GREEN} />,
        };
        const newFlags = flags.slice();
        newFlags.push(flag);
        setFlags(newFlags);
    };

    useEffect(() => {
        if (flags.length) setTimeout(() => setFlags([]), 5000);
    }, [flags]);

    function getTypeOption(notificationType) {
        return {
            value: notificationType.id,
            label: notificationType.name,
            color: notificationType.color,
        };
    }

    function getTypesOptions(notificationTypes) {
        return notificationTypes.map(getTypeOption);
    }

    let notificationOptions = [];
    if (!notificationTypesLoading) {
        notificationOptions = getTypesOptions(notificationTypesData.notificationTypes);
    }

    const editedNotificationType = notification?.notificationType ? getTypeOption(notification.notificationType) : null;

    const onEditorChange = (actions) =>
        debounce(() => {
            actions.getValue().then((value) => {
                setDetailDescription(value);
            });
        }, 500);

    const onDetailViewClick = (e) => {
        setShowDetailDescriptionField(e.target.checked);
    };

    async function createNewNotification(data) {
        setSaveLoading(true);
        const payload = {
            widgetId,
            notificationTypeId: data.type ? data.type.value : null,
            title: data.title,
            description: data.description,
            detailDescription,
            imageLink: data['image-link'],
            showDetailView: data['detail-view'],
            showFeedbackVoting: data['show-feedback-voting'],
            showFeedbackField: false,
        };
        const result = await createNotification({ variables: { payload } });
        setSaveLoading(false);
        addFlag('created');
        setNotificationForUpdate(result.data.createNotification);
    }

    async function updateExistingNotification(data) {
        setSaveLoading(true);
        const { id } = notification;
        const payload = {
            notificationTypeId: data.type ? data.type.value : null,
            title: data.title,
            description: data.description,
            detailDescription,
            imageLink: data['image-link'],
            showDetailView: data['detail-view'],
            showFeedbackVoting: data['show-feedback-voting'],
            showFeedbackField: data['show-feedback-message-field'],
        };
        await updateNotification({ variables: { id, payload } });
        setSaveLoading(false);
        setIsSaveModalOpen(false);
        addFlag('updated');
    }

    const onSave = async (data) => {
        if (!notification) {
            createNewNotification(data);
        } else if (notification.publishedAt) {
            setFormData(data);
            setIsSaveModalOpen(true);
        } else {
            updateExistingNotification(data);
        }
    };

    async function onPublishToggle() {
        setPublishLoading(true);
        const { id } = notification;
        let flagType = '';
        const result = await toggleNotificationPublished({ variables: { id } });
        const { publishedAt } = result.data.toggleNotificationPublished;
        if (publishedAt !== null) {
            flagType = 'published';
        } else {
            flagType = 'unpublished';
        }
        notification.publishedAt = publishedAt;
        addFlag(flagType);
        setPublishLoading(false);
        setIsPublishModalOpen(false);
        setNotificationForUpdate(notification);
    }

    async function onRemoveNotification() {
        setDeleteLoading(true);
        const { id } = notification;
        await deleteNotification({ variables: { id } });
        setIsRemoveModalOpen(false);
        addFlag('deleted');
        setDeleteLoading(false);
        setNotificationForUpdate(null);
        closePage();
    }

    return (
        <Page>
            <Grid layout="fluid">
                <GridColumn medium={12}>
                    <FlexContainer id="attach">
                        <PageWrapper>
                            <Form onSubmit={onSave}>
                                {({ formProps }) => (
                                    <form {...formProps} name="notification-fields">
                                        <ArrowLabelWrapper>
                                            <div className="arrow-label">Back to notification list</div>
                                            <div>
                                                <Button
                                                    appearance="primary"
                                                    isDisabled={publishLoading || !notification}
                                                    onClick={() => {
                                                        if (notification.publishedAt) {
                                                            return onPublishToggle();
                                                        }
                                                        return setIsPublishModalOpen(true);
                                                    }}
                                                >
                                                    {notification?.publishedAt ? 'Unpublish' : 'Publish'}
                                                </Button>
                                                <Button
                                                    style={{ marginLeft: '10px' }}
                                                    type="submit"
                                                    isDisabled={saveLoading}
                                                >
                                                    Save changes
                                                </Button>
                                                <Button
                                                    style={{ marginLeft: '10px' }}
                                                    iconBefore={<TrashIcon />}
                                                    onClick={() => setIsRemoveModalOpen(true)}
                                                />
                                            </div>
                                        </ArrowLabelWrapper>
                                        <ContentWrapper>
                                            <section className="notification-form-wrapper">
                                                <PageHeader
                                                    actions={
                                                        <StatusLabel
                                                            status={notification?.publishedAt ? 'published' : 'draft'}
                                                        />
                                                    }
                                                >
                                                    Notification details
                                                </PageHeader>

                                                <Field
                                                    name="type"
                                                    label="Type"
                                                    id="type"
                                                    defaultValue={type || editedNotificationType}
                                                >
                                                    {({ fieldProps }) => (
                                                        <Select
                                                            {...fieldProps}
                                                            options={notificationOptions}
                                                            isClearable="true"
                                                            placeholder="Choose a Type"
                                                            styles={typesColorStyles}
                                                            onChange={(e) => {
                                                                const typeSet = e;
                                                                typeSet.name = e.label;
                                                                if (e) setType(e);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <Field
                                                    name="title"
                                                    label="Title"
                                                    isRequired
                                                    id="title"
                                                    defaultValue={title || (notification ? notification.title : '')}
                                                >
                                                    {({ fieldProps }) => (
                                                        <TextField
                                                            value={title || (notification ? notification.title : '')}
                                                            {...fieldProps}
                                                            onChange={(e) => {
                                                                setTitle(e.target.value);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <Field
                                                    name="image-link"
                                                    label="Image (insert publicly accessible link)"
                                                    id="image-link"
                                                    defaultValue={image || notification?.imageLink || ''}
                                                >
                                                    {() => (
                                                        <TextField
                                                            value={image || notification?.imageLink || ''}
                                                            onChange={(e) => {
                                                                setImage(e.target.value);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <Field
                                                    name="description"
                                                    label="Description"
                                                    id="description"
                                                    defaultValue={
                                                        description || (notification ? notification.description : '')
                                                    }
                                                >
                                                    {() => (
                                                        <TextArea
                                                            value={
                                                                description ||
                                                                (notification ? notification.description : '')
                                                            }
                                                            onChange={(e) => {
                                                                setDescription(e.target.value);
                                                            }}
                                                        />
                                                    )}
                                                </Field>
                                                <CheckboxField
                                                    name="detail-view"
                                                    defaultIsChecked={
                                                        notification ? notification.showDetailView : false
                                                    }
                                                >
                                                    {({ fieldProps }) => (
                                                        <Fragment>
                                                            <Checkbox
                                                                {...fieldProps}
                                                                onChange={(e) => {
                                                                    fieldProps.onChange(e);
                                                                    onDetailViewClick(e);
                                                                }}
                                                                label="Enable detail view"
                                                            />
                                                        </Fragment>
                                                    )}
                                                </CheckboxField>
                                                {showDetailDescriptionField && (
                                                    <Field
                                                        name="detail-description"
                                                        defaultValue={
                                                            notification ? notification.detailDescription : ''
                                                        }
                                                        label="Detail description"
                                                        id="detail-description"
                                                    >
                                                        {() => (
                                                            <EditorContext>
                                                                <WithEditorActions
                                                                    render={(actions) => (
                                                                        <Editor
                                                                            appearance="comment"
                                                                            allowRule={true}
                                                                            allowTables={true}
                                                                            contentTransformerProvider={(schema) =>
                                                                                new JIRATransformer(schema)
                                                                            }
                                                                            defaultValue={
                                                                                notification
                                                                                    ? notification.detailDescription
                                                                                    : ''
                                                                            }
                                                                            onChange={onEditorChange(actions)}
                                                                            on
                                                                        />
                                                                    )}
                                                                />
                                                            </EditorContext>
                                                        )}
                                                    </Field>
                                                )}
                                                <Fieldset legend="Feedback">
                                                    <CheckboxField
                                                        name="show-feedback-voting"
                                                        defaultIsChecked={
                                                            showFeedbackField !== null
                                                                ? showFeedbackField
                                                                : notification?.showFeedbackVoting
                                                        }
                                                    >
                                                        {() => (
                                                            <Checkbox
                                                                isChecked={
                                                                    showFeedbackField !== null
                                                                        ? showFeedbackField
                                                                        : notification?.showFeedbackVoting
                                                                }
                                                                onChange={() => {
                                                                    setShowFeedbackField(
                                                                        showFeedbackField !== null
                                                                            ? !showFeedbackField
                                                                            : !notification?.showFeedbackVoting,
                                                                    );
                                                                }}
                                                                label="Show feedback voting"
                                                            />
                                                        )}
                                                    </CheckboxField>
                                                </Fieldset>
                                            </section>
                                            <section className="preview-wrapper">
                                                <PageHeader>Preview</PageHeader>
                                                <div style={{ maxWidth: '400px' }}>
                                                    {notification ? (
                                                        <PijenDemo
                                                            imageLink={image || notification.imageLink}
                                                            label=""
                                                            title={title || notification.title}
                                                            description={description || notification.description}
                                                            showFeedbackVoting={
                                                                showFeedbackField !== null
                                                                    ? showFeedbackField
                                                                    : notification.showFeedbackVoting
                                                            }
                                                            type={type || notification.notificationType}
                                                        />
                                                    ) : (
                                                        <PijenDemo
                                                            imageLink={image}
                                                            label=""
                                                            title={title}
                                                            showFeedbackVoting={showFeedbackField || false}
                                                            showFeedbackField={false}
                                                            description={description}
                                                            type={type}
                                                        />
                                                    )}
                                                </div>
                                            </section>
                                        </ContentWrapper>
                                    </form>
                                )}
                            </Form>
                        </PageWrapper>
                    </FlexContainer>
                </GridColumn>
            </Grid>
            {isRemoveModalOpen && (
                <Modal width={510}>
                    <ModalHeader>
                        <ModalTitle appearance="warning">Are you sure you want to delete this notification?</ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <p>Deleting the notification can’t be taken back.</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button appearance="subtle" onClick={() => setIsRemoveModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            autoFocus
                            appearance="warning"
                            onClick={() => onRemoveNotification()}
                            isDisabled={deleteLoading || !notification}
                        >
                            Delete
                        </Button>
                    </ModalFooter>
                </Modal>
            )}
            {isPublishModalOpen && (
                <Modal width={510}>
                    <ModalHeader>
                        <ModalTitle appearance="warning">Publish and save the notification 🚀</ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <p>
                            You’re about to {notification.publishedAt ? 'unpublish' : 'publish'} the notification. All
                            changes will be saved and it’ll then be visible in the production widget.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button appearance="subtle" onClick={() => setIsPublishModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            autoFocus
                            appearance="warning"
                            onClick={() => onPublishToggle()}
                            isDisabled={publishLoading}
                        >
                            Apply
                        </Button>
                    </ModalFooter>
                </Modal>
            )}
            {isSaveModalOpen && (
                <Modal width={510}>
                    <ModalHeader>
                        <ModalTitle appearance="warning">Save changes to published notification</ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <p>
                            You’re about to save changes the published notification. All changes will be saved and it’ll
                            then be visible in the production widget.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button appearance="subtle" onClick={() => setIsSaveModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            autoFocus
                            appearance="warning"
                            onClick={() => updateExistingNotification(formData)}
                            isDisabled={saveLoading}
                        >
                            Save changes
                        </Button>
                    </ModalFooter>
                </Modal>
            )}
            <FlagGroup>
                {flags.map(({ key, ...flag }) => (
                    <Flag key={key} {...flag} />
                ))}
            </FlagGroup>
        </Page>
    );
}

export default React.memo(NotificationDetails);
